<script lang="ts">
export default {
  name: 'CompaniesBenefitsIndex',
}
</script>
<script setup lang="ts">
import { computed } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { KkBox, KkLoading } from '../../../components/base'
import { KkPagination } from '../../../components/ui'
import { KkIndexCard } from '../../../domains/benefits'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useBenefitsStore } from '../../../new_stores/pages/benefits/modules'
import { usePagination } from '../../../composables'
import type { ReadBenefitsParams } from '../../../new_stores/pages/benefits/modules'

const { t } = useI18n()
const store = useStore()
const route = useRoute()
const benefitsStore = useBenefitsStore()

const {
  benefitsLoading: benefitsLoading,
  benefits: benefits,
  benefitsMeta: benefitsMeta
} = storeToRefs(benefitsStore)
const { readBenefits } = benefitsStore

const {
  currentPage,
  displayNumber,
  setCurrentPage,
  setDisplayNumber
} = usePagination()

const currentCompanyUuid = computed<string>(() => route.params.company_uuid as string)
const company = computed(() => store.getters['companiesCompany/company'])

const loading = computed<boolean>(() => benefitsLoading.value)

const readCompany = (companyUuid: string): Promise<void> => store.dispatch('companiesCompany/readCompany', companyUuid)

const resetPage = () => {
  setCurrentPage(1)
}
const benefitsQuery = computed<ReadBenefitsParams>(() => {
  return {
    per: displayNumber.value,
    page: currentPage.value,
  }
})
const changeDisplayNumber = (e: Event) => {
  const inputElement = e.target as HTMLInputElement
  resetPage()
  setDisplayNumber(Number(inputElement.value))
  readBenefits({ companyUuid: currentCompanyUuid.value, params: benefitsQuery.value })
}

const goPrevPage = (): void => {
  setCurrentPage(currentPage.value - 1)
  changePage()
}
const goNextPage = (): void => {
  setCurrentPage(currentPage.value + 1)
  changePage()
}
const changePage = (): void => {
  window.scrollTo({ top: 0 })
  readBenefits({ companyUuid: currentCompanyUuid.value, params: benefitsQuery.value })
}

const load = (): void => {
  readCompany(currentCompanyUuid.value)
  readBenefits({ companyUuid: currentCompanyUuid.value, params: benefitsQuery.value })
}
load()
</script>
<template>
  <KkBox
    tag="div"
    border
  >
    <template #header>
      <h3>{{ t('pages.CompaniesBenefits.benefitsFor', { companyName: company.name } ) }}</h3>
    </template>
    <template #content>
      <KkLoading v-if="loading" />
      <div v-else>
        <p class="benefitsCount">{{ t('pages.CompaniesBenefits.benefitsCount', { count: benefitsMeta.totalCount } ) }}</p>
        <div class="cardContainer">
          <router-link
            v-for="(benefit, index) in benefits"
            :key="index"
            class="cardLink"
            :to="{ name: 'CompaniesBenefitsDetail', params: { benefit_id: benefit.id }}"
          >
            <KkIndexCard
              :benefit="benefit"
            />
          </router-link>
        </div>
        <KkPagination
          :loading="loading"
          :current-page="currentPage"
          :display-number="Number(displayNumber)"
          :contents-length="benefits.length"
          :total-pages="Number(benefitsMeta.totalPages)"
          :minimum-unit="25"
          @onChangeDisplayNumber="changeDisplayNumber($event)"
          @goPrevPage="goPrevPage"
          @goNextPage="goNextPage"
        />
      </div>
    </template>
  </KkBox>
</template>
<style lang="scss" scoped>
$sp: 768px;
@mixin sp {
  @media (max-width: ($sp)) {
    @content;
  }
}

.benefitsCount {
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 14px;
}
.cardContainer {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
  @include sp {
    grid-template-columns: repeat(1, 1fr);
    gap: 20px;
  }
}
.cardLink {
  text-decoration: none;
}
</style>
